/**
 * KnowledgeSpaceDetailPage - Three-column layout for Knowledge Spaces
 *
 * Layout:
 * ┌─────────────┬──────────────┬─────────────┐
 * │  SOURCES    │    CHAT      │   STUDIO    │
 * │  (Left)     │  (Middle)    │  (Right)    │
 * └─────────────┴──────────────┴─────────────┘
 *
 * Features:
 * - Left: Upload and select sources (documents)
 * - Middle: Q&A chat with inline citations
 * - Right: Saved notes and templates (summary generation)
 */

import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KnowledgeDocument, KnowledgeSpace } from '@deep-agents/shared-types';
import { Sidebar } from '../../components/layout';
import { SourcesColumn } from '../../components/knowledge/SourcesColumn';
import { ChatColumn } from '../../components/knowledge/ChatColumn';
import { StudioColumn, SavedNote } from '../../components/knowledge/StudioColumn';

export function KnowledgeSpaceDetailPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();

  // === STATE ===
  const [space, setSpace] = useState<KnowledgeSpace | null>(null);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [notes, setNotes] = useState<SavedNote[]>([]);
  const [isLoadingSpace, setIsLoadingSpace] = useState(true);
  const [isLoadingNotes, setIsLoadingNotes] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection state: which documents are selected for context
  const [selectedDocuments, setSelectedDocuments] = useState<Record<string, boolean>>({});

  // Highlighted document (for citation click scroll/highlight)
  const [highlightedDocumentId, setHighlightedDocumentId] = useState<string | null>(null);

  // Summary generation state
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  // === DATA FETCHING ===

  // Load space and documents
  const loadSpace = useCallback(async () => {
    if (!spaceId) return;
    setIsLoadingSpace(true);
    setError(null);

    try {
      const [spaceRes, docsRes] = await Promise.all([
        fetch(`/api/kb/spaces/${spaceId}`, { credentials: 'include' }),
        fetch(`/api/kb/spaces/${spaceId}/documents`, { credentials: 'include' }),
      ]);

      if (!spaceRes.ok) {
        const data = await spaceRes.json();
        throw new Error(data?.error || 'Failed to load space');
      }

      const spaceData = await spaceRes.json();
      setSpace(spaceData?.data?.space || null);

      if (docsRes.ok) {
        const docsData = await docsRes.json();
        const loadedDocs = docsData?.data?.documents || [];
        setDocuments(loadedDocs);

        // Auto-select all ready documents by default
        const initialSelection: Record<string, boolean> = {};
        loadedDocs.forEach((doc: KnowledgeDocument) => {
          if (doc.status === 'ready') {
            initialSelection[doc.id] = true;
          }
        });
        setSelectedDocuments(initialSelection);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load space';
      setError(message);
    } finally {
      setIsLoadingSpace(false);
    }
  }, [spaceId]);

  // Load saved notes
  const loadNotes = useCallback(async () => {
    if (!spaceId) return;
    setIsLoadingNotes(true);

    try {
      const response = await fetch(`/api/kb/spaces/${spaceId}/notes`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setNotes(data?.data?.notes || []);
      }
    } catch (err) {
      console.error('Failed to load notes:', err);
      // Don't set error state - notes are optional
    } finally {
      setIsLoadingNotes(false);
    }
  }, [spaceId]);

  // Initial load
  useEffect(() => {
    loadSpace();
    loadNotes();
  }, [loadSpace, loadNotes]);

  // === SELECTION HANDLERS ===

  const handleSelectionChange = useCallback((documentId: string, selected: boolean) => {
    setSelectedDocuments((prev) => ({
      ...prev,
      [documentId]: selected,
    }));
  }, []);

  const handleSelectAll = useCallback((selected: boolean) => {
    const newSelection: Record<string, boolean> = {};
    documents.forEach((doc) => {
      if (doc.status === 'ready') {
        newSelection[doc.id] = selected;
      }
    });
    setSelectedDocuments(newSelection);
  }, [documents]);

  // Get list of selected document IDs for chat context
  const selectedDocumentIds = useMemo(() => {
    return Object.entries(selectedDocuments)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
  }, [selectedDocuments]);

  // === CITATION HANDLER ===

  const handleCitationClick = useCallback((citationDocumentId: string) => {
    // Gemini returns the document title as the citation documentId
    // Find the actual document by matching the title
    const matchingDoc = documents.find(
      (doc) => doc.title === citationDocumentId || doc.title.includes(citationDocumentId)
    );

    if (matchingDoc) {
      // Set the highlighted document - this will trigger scroll and highlight in SourcesColumn
      setHighlightedDocumentId(matchingDoc.id);

      // Clear highlight after 2 seconds
      setTimeout(() => {
        setHighlightedDocumentId(null);
      }, 2000);
    } else {
      console.log('Citation document not found:', citationDocumentId);
    }
  }, [documents]);

  // === NOTES HANDLERS ===

  const handleCreateNote = useCallback(async (title: string, content: string) => {
    if (!spaceId) return;

    const response = await fetch(`/api/kb/spaces/${spaceId}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title, content, sourceType: 'manual' }),
    });

    if (response.ok) {
      await loadNotes();
    } else {
      const data = await response.json();
      throw new Error(data?.error || 'Failed to create note');
    }
  }, [spaceId, loadNotes]);

  const handleUpdateNote = useCallback(async (noteId: string, title: string, content: string) => {
    if (!spaceId) return;

    const response = await fetch(`/api/kb/spaces/${spaceId}/notes/${noteId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title, content }),
    });

    if (response.ok) {
      await loadNotes();
    } else {
      const data = await response.json();
      throw new Error(data?.error || 'Failed to update note');
    }
  }, [spaceId, loadNotes]);

  const handleDeleteNote = useCallback(async (noteId: string) => {
    if (!spaceId) return;

    const response = await fetch(`/api/kb/spaces/${spaceId}/notes/${noteId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (response.ok) {
      await loadNotes();
    } else {
      const data = await response.json();
      throw new Error(data?.error || 'Failed to delete note');
    }
  }, [spaceId, loadNotes]);

  // === SUMMARY GENERATION ===

  const handleGenerateSummary = useCallback(async (): Promise<string | null> => {
    if (!spaceId) return null;

    setIsGeneratingSummary(true);

    try {
      // Ask for a summary using the knowledge space
      const response = await fetch(`/api/kb/spaces/${spaceId}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question: 'Please provide a comprehensive summary of all the key points, main ideas, and important information from the documents in this knowledge space.',
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.error || 'Failed to generate summary');
      }

      const data = await response.json();
      return data?.data?.answer || null;
    } catch (err) {
      console.error('Summary generation failed:', err);
      return null;
    } finally {
      setIsGeneratingSummary(false);
    }
  }, [spaceId]);

  // === RENDER ===

  if (!spaceId) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 text-gray-900">
        Space ID is required
      </div>
    );
  }

  return (
    <div className="knowledge-space-page">
      <Sidebar />

      <main className="main-content">
        {/* Header */}
        <header className="page-header">
          <div className="header-left">
            <button
              type="button"
              className="back-button"
              onClick={() => navigate('/knowledge')}
            >
              ← Back
            </button>
            <h1 className="page-title">
              {space?.name || 'Knowledge Space'}
            </h1>
          </div>
          {space && (
            <span className="space-badge">
              {space.type.toUpperCase()}
            </span>
          )}
        </header>

        {/* Error message */}
        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        {/* Three-tile layout */}
        <div className="tiles-container">
          {/* Left: Sources Tile */}
          <div className="tile sources-tile">
            <SourcesColumn
              spaceId={spaceId}
              documents={documents}
              selectedDocuments={selectedDocuments}
              onSelectionChange={handleSelectionChange}
              onSelectAll={handleSelectAll}
              isLoading={isLoadingSpace}
              onDocumentUploaded={loadSpace}
              highlightedDocumentId={highlightedDocumentId}
            />
          </div>

          {/* Middle: Chat Tile */}
          <div className="tile chat-tile">
            <ChatColumn
              spaceId={spaceId}
              selectedDocumentIds={selectedDocumentIds}
              onCitationClick={handleCitationClick}
            />
          </div>

          {/* Right: Studio Tile */}
          <div className="tile studio-tile">
            <StudioColumn
              spaceId={spaceId}
              notes={notes}
              isLoading={isLoadingNotes}
              onCreateNote={handleCreateNote}
              onUpdateNote={handleUpdateNote}
              onDeleteNote={handleDeleteNote}
              onGenerateSummary={handleGenerateSummary}
              isGeneratingSummary={isGeneratingSummary}
            />
          </div>
        </div>
      </main>

      <style>{`
        /* === LIGHT THEME BASE === */
        .knowledge-space-page {
          display: flex;
          min-height: 100vh;
          background: #f8fafc;
        }

        .main-content {
          flex: 1;
          margin-left: 240px;
          display: flex;
          flex-direction: column;
          height: 100vh;
          background: #f8fafc;
        }

        /* === HEADER - LIGHT THEME === */
        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          flex-shrink: 0;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .back-button {
          padding: 6px 12px;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          color: #64748b;
          cursor: pointer;
          transition: all 0.15s;
        }

        .back-button:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
          color: #334155;
        }

        .page-title {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .space-badge {
          padding: 4px 10px;
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.2);
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          color: #6366f1;
        }

        .error-banner {
          padding: 12px 24px;
          background: rgba(220, 38, 38, 0.1);
          border-bottom: 1px solid rgba(220, 38, 38, 0.2);
          color: #f87171;
          font-size: 13px;
          flex-shrink: 0;
        }

        /* === THREE TILE LAYOUT === */
        .tiles-container {
          display: grid;
          grid-template-columns: 320px 1fr 340px;
          gap: 16px;
          flex: 1;
          padding: 16px 24px 24px;
          overflow: hidden;
        }

        .tile {
          background: #0A0A0C;
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 16px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          position: relative;
        }

        .sources-tile {
          min-width: 280px;
        }

        .chat-tile {
          min-width: 400px;
        }

        .studio-tile {
          min-width: 300px;
        }

        /* === RESPONSIVE === */
        @media (max-width: 1200px) {
          .tiles-container {
            grid-template-columns: 280px 1fr 300px;
            gap: 12px;
          }
        }

        @media (max-width: 1024px) {
          .main-content {
            margin-left: 200px;
          }

          .tiles-container {
            grid-template-columns: 1fr;
            grid-template-rows: auto 1fr auto;
            gap: 12px;
          }

          .tile {
            max-height: 400px;
          }

          .chat-tile {
            max-height: none;
            min-height: 500px;
          }
        }
      `}</style>
    </div>
  );
}
